import { Octokit } from "@octokit/core";
import db from "services/firebase";
import { dismissLoadingToast, showErrorToast, showLoadingToast, showSuccessToast, updateLoadingToast } from "app/components/ShowToast";
import { doc, collection, getDocs, query, where, addDoc, serverTimestamp }  from "firebase/firestore";
import { addIntegrationTests } from "./services/integrationTestsService";
interface Repository {
  name: string;
  html_url: string;
  private: boolean;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
  pushed_at: string | null;
  size: number;
  stargazers_count: number;
  watchers_count: number;
  language: string | null;
  forks_count: number;
  open_issues_count: number;
  default_branch: string | null;
  owner?: {
    login: string;
    id: number;
    html_url: string;
    type: string;
  };
}

interface GitHubIntegrationResponse {
  status: number;
  success?: boolean;
  data?: Repository[];
  error?: string;
  repositories?: Repository[];
  organizations?: any[];
}

interface GitHubIntegrationData {
  accessToken: string;
  tenantId: string;
  uid: string;
  userName?: string;
  email?: string;
}

export const createGitHubIntegration = (accessToken: string) => {
  const octokit = new Octokit({
    auth: accessToken,
  });

  const getUserOrganizations = async (): Promise<GitHubIntegrationResponse> => {
    try {
      const allRepos: Repository[] = [];
      const orgs: any[] = [];

      const { data: userRepos } = await octokit.request("GET /user/repos", {
        type: "all",
        sort: "updated",
        direction: "desc",
      });

      const mappedUserRepos: Repository[] = userRepos.map((repo: any) => ({
        name: repo.name,
        html_url: repo.html_url,
        private: repo.private,
        description: repo.description,
        created_at: repo.created_at || null,
        updated_at: repo.updated_at || null,
        pushed_at: repo.pushed_at || null,
        size: repo.size || 0,
        stargazers_count: repo.stargazers_count || 0,
        watchers_count: repo.watchers_count || 0,
        language: repo.language || null,
        forks_count: repo.forks_count || 0,
        open_issues_count: repo.open_issues_count || 0,
        default_branch: repo.default_branch || null,
        owner: repo.owner
          ? {
              login: repo.owner.login,
              id: repo.owner.id,
              html_url: repo.owner.html_url,
              type: repo.owner.type,
            }
          : undefined,
      }));

      allRepos.push(...mappedUserRepos);

      const { data: fetchedOrgs } = await octokit.request("GET /user/orgs");
      orgs.push(...fetchedOrgs);

      for (const org of orgs) {
        const { data: repos } = await octokit.request("GET /orgs/{org}/repos", {
          org: org.login,
          type: "all",
          sort: "updated",
          direction: "desc",
        });

        const mappedRepos: Repository[] = repos.map((repo: any) => ({
          name: repo.name,
          html_url: repo.html_url,
          private: repo.private,
          description: repo.description,
          created_at: repo.created_at || null,
          updated_at: repo.updated_at || null,
          pushed_at: repo.pushed_at || null,
          size: repo.size || 0,
          stargazers_count: repo.stargazers_count || 0,
          watchers_count: repo.watchers_count || 0,
          language: repo.language || null,
          forks_count: repo.forks_count || 0,
          open_issues_count: repo.open_issues_count || 0,
          default_branch: repo.default_branch || null,
          owner: repo.owner
            ? {
                login: repo.owner.login,
                id: repo.owner.id,
                html_url: repo.owner.html_url,
                type: repo.owner.type,
              }
            : undefined,
        }));

        allRepos.push(...mappedRepos);
      }

      return {
        status: 200,
        data: allRepos,
        organizations: orgs,
      };
    } catch (error: any) {
      return {
        status: error.status || 500,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  const getOrgRepositories = async (orgLogin: string): Promise<GitHubIntegrationResponse> => {
    try {
      const { data: repos } = await octokit.request("GET /orgs/{org}/repos", {
        org: orgLogin,
        type: "all",
        sort: "updated",
        direction: "desc",
        per_page: 100,
      });

      const mappedRepos: Repository[] = repos.map((repo: any) => ({
        name: repo.name,
        html_url: repo.html_url,
        private: repo.private,
        description: repo.description,
        created_at: repo.created_at || null,
        updated_at: repo.updated_at || null,
        pushed_at: repo.pushed_at || null,
        size: repo.size || 0,
        stargazers_count: repo.stargazers_count || 0,
        watchers_count: repo.watchers_count || 0,
        language: repo.language || null,
        forks_count: repo.forks_count || 0,
        open_issues_count: repo.open_issues_count || 0,
        default_branch: repo.default_branch || null,
        owner: repo.owner
          ? {
              login: repo.owner.login,
              id: repo.owner.id,
              html_url: repo.owner.html_url,
              type: repo.owner.type,
            }
          : undefined,
      }));

      return {
        status: 200,
        success: true,
        repositories: mappedRepos,
      };
    } catch (error: any) {
      return {
        status: error.status || 500,
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  const check2FA = async (orgName: string) => {
    try {
      const { data } = await octokit.request("GET /orgs/{org}", {
        org: orgName,
      });
      return {
        success: true,
        mfaRequired: data.two_factor_requirement_enabled,
        message: `MFA enforcement status: ${
          data.two_factor_requirement_enabled ? "Enforced" : "Not Enforced"
        }`,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  const checkUser2FA = async (orgName: string) => {
    try {
      const { data: allMembers } = await octokit.request("GET /orgs/{org}/members", {
        org: orgName,
        per_page: 100,
      });
      const { data: membersWithout2FA } = await octokit.request("GET /orgs/{org}/members", {
        org: orgName,
        filter: "2fa_disabled",
        per_page: 100,
      });
  
      const usersWithout2FASet = new Set(membersWithout2FA.map((user: any) => user.login));
  
      const usersWith2FAStatus = await Promise.all(
        allMembers.map(async (user: any) => {
          try {
            const { data: userDetails } = await octokit.request("GET /users/{username}", {
              username: user.login,
            });
  
            const { data: membership } = await octokit.request(
              "GET /orgs/{org}/memberships/{username}",
              {
                org: orgName,
                username: user.login,
              }
            );
  
            return {
              login: user.login,
              id: user.id,
              html_url: user.html_url,
              name: userDetails.name || null,
              has2FA: !usersWithout2FASet.has(user.login),
              role: membership.role || null,
            };
          } catch (error) {
            console.error(`Error fetching details for user ${user.login}:`, error);
            return {
              login: user.login,
              id: user.id,
              html_url: user.html_url,
              name: null,
              has2FA: !usersWithout2FASet.has(user.login),
              role: null,
            };
          }
        })
      );
  
      return {
        success: true,
        users: usersWith2FAStatus,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  const checkForNewRepositories = async (existingRepos: Repository[] = []) => {
    try {
      const response = await getUserOrganizations();
      if (!response.data) {
        return {
          success: false,
          error: "Failed to fetch repositories",
        };
      }

      const existingRepoUrls = new Set(existingRepos.map((repo) => repo?.html_url));
      const newRepos = response.data.filter((repo) => !existingRepoUrls.has(repo.html_url));

      return {
        success: true,
        newRepositories: newRepos.map((repo) => ({
          name: repo.name,
          html_url: repo.html_url,
          private: repo.private,
          description: repo.description,
          created_at: repo.created_at,
          updated_at: repo.updated_at,
          pushed_at: repo.pushed_at,
          size: repo.size,
          stargazers_count: repo.stargazers_count,
          watchers_count: repo.watchers_count,
          language: repo.language,
          forks_count: repo.forks_count,
          open_issues_count: repo.open_issues_count,
          default_branch: repo.default_branch,
          owner: {
            login: repo.owner?.login,
            id: repo.owner?.id,
            html_url: repo.owner?.html_url,
            type: repo.owner?.type,
          },
        })),
      };
    } catch (error: any) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  const saveGitHubIntegration = async (
    data: GitHubIntegrationData,
    repos: Repository[],
    currentUser: any,
    org2FAResults: any[],
    organizations: any[]
  ) => {
    try {
      let isAnyRepoAdded = false;
      let integrationId: string | undefined;
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
      const todaysDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()}/${currentDate.getFullYear()}`;

      if (!data.tenantId || typeof data.tenantId !== 'string' || data.tenantId.trim() === '') {
        throw new Error('Invalid tenantId provided');
      }

      for (const repo of repos) {
        const newAsset = {
          is_repo: true,
          repo_name: repo.name || 'Unnamed Repository',
          repo_link: repo.html_url || '',
          platform: "GitHub",
          visibility: repo.private ? "Private" : "Public",
          description: repo.description || '',
          created_at: repo.created_at || new Date().toISOString(),
          updated_at: repo.updated_at || new Date().toISOString(),
          pushed_at: repo.pushed_at || new Date().toISOString(),
          size: repo.size || 0,
          stars: repo.stargazers_count || 0,
          watchers: repo.watchers_count || 0,
          language: repo.language || '',
          forks: repo.forks_count || 0,
          open_issues: repo.open_issues_count || 0,
          default_branch: repo.default_branch || 'main',
          owner: {
            login: repo.owner?.login || '',
            id: repo.owner?.id || 0,
            url: repo.owner?.html_url || '',
            type: repo.owner?.type || 'Organization',
          },
          tenantId: data.tenantId,
          uid: data.uid,
          createdAt: serverTimestamp(),
          audit_scope: true,
        };

        const tenantRef = doc(db, "tenants", data.tenantId);
        const policyRef = collection(tenantRef, "assets");

        const querySnapshot = await getDocs(
          query(policyRef, where("repo_link", "==", repo.html_url))
        );

        if (querySnapshot.empty) {
          await addDoc(policyRef, newAsset);
          // isAnyRepo++;
          const activitiesRef = collection(tenantRef, "activities");
          await addDoc(activitiesRef, {
            message: `${data.userName || 'Unknown User'} added a new asset '${repo.name}'`,
            timestamp: serverTimestamp(),
            tenantId: data.tenantId,
            uid: data.uid,
            action: "Asset added",
            target: "Asset Management",
            email: data.email || '',
            username: data.userName || 'Unknown User',
            date: todaysDate,
          });
        }
      }

      const tenantRef = doc(db, "tenants", data.tenantId);
      const githubRef = collection(tenantRef, "integrations");
      const vendorsRef = collection(tenantRef, "vendors");
      const integrationSnapshot = await getDocs(query(githubRef, where('name', '==' , 'GitHub')));

      if (integrationSnapshot.empty) {
        const newData = {
          accessToken: data.accessToken,
          name: "GitHub",
          group: "Developer Tools",
          img_path: "/integrations/Github.svg",
          status: "Connected",
          connectedAt: formattedDate,
          tenantId: data.tenantId,
          uid: data.uid,
          createdAt: serverTimestamp(),
          org2FAResults,
          organizations,
        };

        const docRef = await addDoc(githubRef, newData);
        integrationId = docRef.id;

        const vendorData = {
          authentication_type: "",
          category: "Developer Tools",
          created_at: serverTimestamp(),
          due_diligence_status: "Incomplete",
          impact_assessment: [],
          logo: [
            {
              name: "github_logo.png",
              size: 3395,
              url: "/integrations/Github.svg",
            },
          ],
          owner: currentUser?.userName || '',
          policy_url: "https://github.com/security",
          reliance: "",
          risk_level: '',
          tenantId: data.tenantId,
          terms_url: "https://github.com/terms",
          two_factor: true,
          uid: data.uid,
          updated_at: serverTimestamp(),
          vendor_name: 'GitHub',
          website_url: 'https://github.com',
          org2FAResults,
        };

        await addDoc(vendorsRef, vendorData);
      } else {
        integrationId = integrationSnapshot.docs[0].id;
      }

      try {
        await addIntegrationTests('GitHub', data.tenantId, currentUser);
      } catch (testError) {
        console.error('Error adding GitHub integration tests:', testError);
      }

      return { success: true, reposAdded: isAnyRepoAdded, integrationId };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  return {
    getUserOrganizations,
    getOrgRepositories,
    check2FA,
    checkUser2FA,
    checkForNewRepositories,
    saveGitHubIntegration,
  };
};

export const exchangeCodeForGitHubToken = async (
  code: string,
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
) => {
  let toastId;
  try {
    toastId = showLoadingToast({ message: 'Authenticating with GitHub...' });
    const response = await fetch(
      `https://us-central1-slate-134a0.cloudfunctions.net/githubOAuthCallback?code=${encodeURIComponent(code)}`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      let errorMessage = 'Failed to exchange code for token';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        errorMessage = `HTTP error ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const responseText = await response.text();
    let data;
    try {
      data = JSON.parse(responseText);  
    } catch (e) {
      throw new Error('Invalid response format from server');
    }

    if (!data.access_token) {
      throw new Error('No valid access token received from server');
    }

    updateLoadingToast(toastId, {
      message: 'Token received, setting up integration...',
      type: 'info',
      isLoading: true,
    });

    localStorage.setItem('github_access_token', data.access_token);

    const integrationId = await fetchAndSaveGitHubRepositories(
      data.access_token,
      data.organizations, 
      currentUser,
      navigate
    );

    dismissLoadingToast(toastId);
    showSuccessToast({ message: 'Successfully authenticated with GitHub' });

    navigate(`/integrations?id=${integrationId}&service=GitHub`, { replace: true });
  } catch (error: any) {
    showErrorToast({ message: 'GitHub authentication failed, please try again later' });
    dismissLoadingToast(toastId);
    localStorage.removeItem('github_access_token');
    navigate('/integrations', { replace: true });
    throw error;
  }
};

export const fetchAndSaveGitHubRepositories = async (
  accessToken: string,
  organizations:any[],
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
): Promise<string | undefined>  => {
  try {
    let integrationId: string | undefined;
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;

    const tenantRef = doc(db, "tenants", currentUser?.tenantId);
    const githubRef = collection(tenantRef, "integrations");
    const vendorsRef = collection(tenantRef, "vendors");
    const integrationSnapshot = await getDocs(query(githubRef, where('name', '==' , 'GitHub')));

    if (integrationSnapshot.empty) {
      const newData = {
        accessToken: accessToken,
        name: "GitHub",
        group: "Developer Tools",
        img_path: "/integrations/Github.svg",
        status: "Connected",
        connectedAt: formattedDate,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
        // org2FAResults,
        organizations,
      };

      const docRef = await addDoc(githubRef, newData);
      integrationId = docRef.id;

      const vendorData = {
        authentication_type: "",
        category: "Developer Tools",
        created_at: serverTimestamp(),
        due_diligence_status: "Incomplete",
        impact_assessment: [],
        logo: [
          {
            name: "github_logo.png",
            size: 3395,
            url: "/integrations/Github.svg",
          },
        ],
        owner: currentUser?.userName || '',
        policy_url: "https://github.com/security",
        reliance: "",
        risk_level: '',
        tenantId: currentUser?.tenantId,
        terms_url: "https://github.com/terms",
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: 'GitHub',
        website_url: 'https://github.com',
        // org2FAResults,
      };

      await addDoc(vendorsRef, vendorData);
    } else {
      integrationId = integrationSnapshot.docs[0].id;
    }

    // Add integration-specific tests
    try {
      await addIntegrationTests('GitHub', currentUser?.tenantId, currentUser);
    } catch (testError) {
      console.error('Error adding GitHub integration tests:', testError);
      // Don't fail the entire integration if tests fail to add
    }

    return integrationId ;
  } catch (error) {
    showErrorToast({ message: 'Error setting up GitHub integration' });
    navigate('/integrations', { replace: true });
  }
};